import { LitElement, html } from 'lit';
import { API_ENDPOINT, BTC_ADDRESS, ETH_ADDRESS, PAYPAL_CLIENT_ID, SOL_ADDRESS } from '../constants';
import PrettyNaoAnalytics from '../service/AnalyticsService';

// Utility to fetch user balance
async function getUserPointsBalance() {
  const res = await fetch(`${API_ENDPOINT}/user/status`, { credentials: 'include' });
  if (!res.ok) return 0;
  const { points } = await res.json();
  return points;
}

// Emits event and closes modal
function triggerPurchaseComplete() {
  document.dispatchEvent(new CustomEvent('purchase-complete'));
  document.getElementById('purchase-points-modal')?.close();
  document.getElementById('product-checkout-confirmation')?.close();
  document.getElementById('payment-verification-modal')?.showModal();
}

function showToast(message) {
  const toast = document.createElement('div');
  toast.className = 'toast toast-top toast-end';
  toast.innerHTML = `<div class="alert alert-success"><span>${message}</span></div>`;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 3000);
}

export class ProductCheckoutConfirmation extends LitElement {
  static properties = {
    productId: { type: String, attribute: 'product-id' },
  };

  constructor() {
    super();
    this.productId = '';
    this.purchaseEndpoint = `${API_ENDPOINT}/shop/order`;
    this.total = 0;
    this.userPoints = 0;
    this.details = {};
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadUserPoints();
    document.getElementById('open-confirmation-btn')?.addEventListener('click', () => this.open());
  }

  async loadUserPoints() {
    this.userPoints = await getUserPointsBalance();
  }

  open() {
    const form = document.getElementById('product-config-form');
    const data = new FormData(form);

    this.details = {
      product_id: this.productId,
      variant_id: data.get('variant'),
      fields: {},
    };

    let total = parseInt(form.querySelector(`option[value="${this.details.variant_id}"]`)?.dataset.points || '0');

    for (let [key, value] of data.entries()) {
      if (key !== 'variant') {
        const fieldEl = form.querySelector(`[name='${key}'][value='${value}']`);
        const points = fieldEl?.dataset.points ? parseInt(fieldEl.dataset.points) : 0;
        this.details.fields[key] = value;
        total += points;
      }
    }

    this.total = total;
    this.requestUpdate();
    document.getElementById('product-checkout-confirmation')?.showModal();
  }

  async confirmPurchase() {
    const res = await fetch(this.purchaseEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...this.details,
        total: this.total,
      }),
    });

    if (res.ok) {
      triggerPurchaseComplete();
      showToast("✅ Your order has been submitted.");
    } else {
      const error = await res.text();
      alert("❌ Failed to submit your order: " + error);
    }
  }

  render() {
    return html`
      <dialog id="product-checkout-confirmation" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${__Lang__.product_confirm_title}</h3>
          <p class="my-4">${__Lang__.product_confirm_total}: <span class="font-bold">${this.total}</span> 💎</p>
          <p>${__Lang__.product_confirm_balance}: ${this.userPoints} 💎</p>

          <div class="modal-action">
            <button class="btn btn-ghost" onclick="document.getElementById('product-checkout-confirmation').close()">${__Lang__.modal_cancel}</button>
            ${this.userPoints >= this.total
        ? html`<button class="btn btn-primary" @click=${this.confirmPurchase.bind(this)}>${__Lang__.product_confirm_button}</button>`
        : html`<button class="btn btn-accent" @click=${() => {
          document.getElementById('product-checkout-confirmation').close();
          document.getElementById('purchase-points-modal')?.showModal();
        }}>${__Lang__.product_buy_points}</button>`}
          </div>
        </div>
      </dialog>
    `;
  }
}

customElements.define('product-checkout-confirmation', ProductCheckoutConfirmation);

export class PurchasePointsModal extends LitElement {
  static properties = {
    pointsUrl: { type: String, attribute: 'points-url' },
  };

  constructor() {
    super();
    this.pointsUrl = '/user/status';
    this.notifyEndpoint = `${API_ENDPOINT}/payment/points/notify-manual`;
    this.network = 'solana';
    this.token = 'SOL';
    this.selectedPack = 0;
    this.walletMap = {
      solana: {
        address: SOL_ADDRESS,
        qr: '/static/images/wallets/sol.png',
        tokens: ['SOL', 'USDC', 'USDT']
      },
      eth: {
        address: ETH_ADDRESS,
        qr: '/static/images/wallets/eth.png',
        tokens: ['ETH', 'USDC', 'USDT']
      },
      btc: {
        address: BTC_ADDRESS,
        qr: '/static/images/wallets/btc.png',
        tokens: ['BTC']
      },
    };
    this.pointPacks = [
      { points: 25, price: 299 },
      { points: 60, price: 499 },
      { points: 140, price: 899 },
      { points: 300, price: 1699 },
      { points: 700, price: 3299 },
    ];
    this.expanded = 'yape'
  }

  createRenderRoot() {
    return this;
  }

  async notifyPayment(method: string) {
    PrettyNaoAnalytics.getPlausible()?.trackEvent('points_checkout_notify_manual_payment', {
      props: {
        method
      },
    })

    const body = { method, network: this.network, token: this.token, points: this.selectedPack, price: this.selectedPackPrice };
    const res = await fetch(this.notifyEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });

    if (res.ok) triggerPurchaseComplete();
    else alert('❌ Payment notification failed');
  }

  copyAddressToClipboard() {
    const text = this.walletMap[this.network].address;
    navigator.clipboard.writeText(text).then(() => {
      alert("📋 Address copied to clipboard!");
    });
  }

  expand(name) {
    this.expanded = name;
    this.requestUpdate();
  }

  connectedCallback() {
    super.connectedCallback();

    const paypalScript = document.createElement('script');
    paypalScript.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}`;
    paypalScript.onload = () => {
      window.paypal.Buttons({
        style: { shape: 'rect', color: 'gold', layout: 'vertical', label: 'pay' },
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{ amount: { value: (this.selectedPackPrice / 100).toString() } }]
          });
        },
        onApprove: async (data, actions) => {
          const details = await actions.order.capture();
          const res = await fetch(`${API_ENDPOINT}/paypal-points-purchase`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ orderID: data.orderID, points: this.selectedPack })
          });

          if (res.ok) {
            const { sale_id } = await res.json();
            this.pollConfirmation(sale_id);
          } else {
            alert("❌ Error recording PayPal payment.");
          }
        },
        onError: (err) => {
          console.error("PayPal error:", err);
          alert("❌ PayPal payment failed.");
        }
      }).render('#paypal-button-container-points');
    };
    document.body.appendChild(paypalScript);
  }

  async pollConfirmation(saleId) {
    const loader = document.createElement('div');
    loader.className = "flex justify-center items-center mt-4";
    loader.innerHTML = `<span class="loading loading-spinner loading-lg"></span>`;
    const target = document.getElementById("paypal-button-container-points");
    target?.appendChild(loader);

    let retries = 0;
    while (retries < 50) {
      await new Promise(r => setTimeout(r, 3000));
      const res = await fetch(`${API_ENDPOINT}/paypal-points-confirm/${saleId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const { confirmed } = await res.json();
        if (confirmed) {
          loader.remove();
          showToast("✅ Points successfully added to your account.");
          triggerPurchaseComplete();
          return;
        }
      }
      retries++;
    }
    loader.remove();
  }

  render() {
    return html`
      <dialog id="purchase-points-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${__Lang__.purchase_points_title}</h3>
          <p class="text-sm mb-4">${__Lang__.purchase_points_select_pack}</p>

          <div class="flex flex-col gap-3 mb-6">
            ${this.pointPacks.map(p => html`
              <button class="btn btn-outline ${this.selectedPack === p.points ? 'btn-accent' : ''}" @click=${() => {
        this.selectedPack = p.points;
        this.selectedPackPrice = p.price;
        this.requestUpdate();

        PrettyNaoAnalytics.getPlausible()?.trackEvent('points_checkout_select_pack', {
          props: {
            pack: p.points,
          },
        })
      }}>
                💎 ${p.points} — $${p.price / 100}
              </button>
            `)}
          </div>

          ${html`
            <div class="${this.selectedPack > 0 ? "" : "hidden"}">
              <!-- Yape -->
              <div class="collapse collapse-arrow bg-base-200 ${this.expanded === 'yape' ? 'collapse-open' : ''}">
                <input type="radio" name="payment-method" .checked=${this.expanded === 'yape'} @change=${() => this.expand('yape')} />
                <div class="collapse-title font-medium">
                  ${__Lang__.purchase_points_yape_title}
                </div>
                <div class="collapse-content">
                  <img src="/static/images/yape.webp" alt="Yape QR" class="w-32 h-32 mx-auto mb-2 rounded" />
                  <button class="btn btn-secondary w-full" @click=${() => this.notifyPayment('yape')}>
                    ${__Lang__.purchase_points_paid_yape}
                  </button>
                </div>
              </div>

              <!-- Crypto -->
              <div class="collapse collapse-arrow bg-base-200 ${this.expanded === 'crypto' ? 'collapse-open' : ''}">
                <input type="radio" name="payment-method" .checked=${this.expanded === 'crypto'} @change=${() => this.expand('crypto')} />
                <div class="collapse-title font-medium">
                  ${__Lang__.purchase_points_crypto_title}
                </div>
                <div class="collapse-content">
                  <label>${__Lang__.purchase_points_network}</label>
                  <select class="select select-bordered w-full mt-1" @change=${e => { this.network = e.target.value; this.token = this.walletMap[this.network].tokens[0]; this.requestUpdate(); }}>
                    <option value="solana">Solana</option>
                    <option value="eth">Ethereum</option>
                    <option value="btc">Bitcoin</option>
                  </select>

                  <label class="mt-3 block">${__Lang__.purchase_points_token}</label>
                  <select class="select select-bordered w-full mt-1" @change=${e => this.token = e.target.value}>
                    ${this.walletMap[this.network].tokens.map(t => html`<option value="${t}">${t}</option>`)}
                  </select>

                  <div class="text-center my-4">
                    <img class="mx-auto w-32 h-32" src="${this.walletMap[this.network].qr}" alt="QR Code" />
                    <div class="flex flex-col lg:flex-row justify-center items-center gap-2 mt-2">
                      <code class="text-xs break-all">${this.walletMap[this.network].address}</code>
                      <button class="btn btn-sm btn-outline" @click=${this.copyAddressToClipboard}>📋</button>
                    </div>
                  </div>

                  <button class="btn btn-primary w-full" @click=${() => this.notifyPayment('crypto')}>
                    ${__Lang__.purchase_points_paid_crypto}
                  </button>
                </div>
              </div>

              <!-- PayPal -->
              <div class="collapse collapse-arrow bg-base-200 ${this.expanded === 'paypal' ? 'collapse-open' : ''}">
                <input type="radio" name="payment-method" .checked=${this.expanded === 'paypal'} @change=${() => this.expand('paypal')} />
                <div class="collapse-title font-medium">
                  ${__Lang__.purchase_points_paypal_title}
                </div>
                <div class="collapse-content">
                  <div class="my-4" id="paypal-button-container-points"></div>
                </div>
              </div>
            </div>
          `}

          <div class="modal-action">
            <button class="btn btn-ghost" onclick="document.getElementById('purchase-points-modal').close()">
              ${__Lang__.modal_cancel}
            </button>
          </div>
        </div>
      </dialog>
    `;
  }
}

customElements.define('purchase-points-modal', PurchasePointsModal);

